import React, { createContext, useCallback, useContext, useEffect } from 'react'
import { ConditionGroupType } from './PatternTreeContext'
import { useApplicationSuiteContext } from '../../../contexts/ApplicationSuiteProvider'
import { FeatureTags } from '../../../common/access/productFeatureTags'
import Indicator from '../condition/indicators/types/Indicator'
import Open from '../condition/indicators/candle/Open'
import High from '../condition/indicators/candle/High'
import Low from '../condition/indicators/candle/Low'
import Close from '../condition/indicators/candle/Close'
import Value from '../condition/indicators/Value'
import gtfIndicators from '../condition/indicators/thirdparty/gtf'
import standardIndicators from '../condition/indicators/standard'
import { useCollectionData } from 'react-firebase-hooks/firestore'
import { collection, orderBy, query, where } from 'firebase/firestore'
import { firebase_firestore } from '../../../common/firebaseConfig'
import useCustomIndicators from '../../../hooks/useCustomIndicators'
import { buildCompositeRegistryEntry } from '../../../common/utils/composite'
import { CustomIndicatorDoc } from '../../../types/composite'
import { PatternSidePanelProvider } from './PatternSidePanelContext'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

type AtPostTrigger = {
  chartDetails: {
    symbol: string
    periodicity: number
    timeframe: string
  }
  type: 'atPost'
}

type AnytimeTrigger = {
  chartDetails: {
    symbol: string
    periodicity: number
    timeframe: string
  }
  type: 'anytime'
}

type TimeBeforePostTrigger = {
  chartDetails: {
    symbol: string
    periodicity: number
    timeframe: string
  }
  type: 'timeBeforePost'
  value: number
  timeframe: 'minute' | 'hour'
}

export type Trigger = AtPostTrigger | AnytimeTrigger | TimeBeforePostTrigger

export type Pattern = {
  id?: string
  name: string
  buyConditionGroups: ConditionGroupType[]
  sellConditionGroups: ConditionGroupType[]
  active?: boolean
  layoutId?: string
  link?: string
  author?: string
  gcid?: string
  message?: string
  version?: number
  tradingHouse?: string
  alertVersion?: number
  versions?: { [versionNum: number]: { notes: string } }[]
  starred?: boolean
  trigger: Trigger
  source?: string
  original?: {
    gcid: string
    patternId: string
    version?: number
  }
}

export const defaultPattern: Pattern = {
  name: 'New Pattern',
  message: '',
  layoutId: '',
  version: 1,
  alertVersion: 1,
  buyConditionGroups: [],
  sellConditionGroups: [],
  trigger: {
    type: 'atPost',
    chartDetails: {
      symbol: 'EURUSD',
      periodicity: 1,
      timeframe: 'minute',
    },
  },
  tradingHouse: 'greenchart',
}

const defaultStartingIndicators = [Open, High, Low, Close, Value]

export function PatternProvider({ children }: any) {
  const { tradingHouse, usermeta, hasFeature } = useApplicationSuiteContext()

  const [startingIndicators, setStartingIndicators] = React.useState<Indicator[]>([])
  const [startingGroupIndicators, setStartingGroupIndicators] = React.useState<
    { label: string; indicators: Indicator[] }[]
  >([])
  const [saveAsStandardPattern, setSaveAsStandardPattern] = React.useState<boolean>(false)
  const [searchTerm, setSearchTerm] = React.useState('')
  const [focusedBacktest, setFocusedBacktest] = React.useState(null)
  const [backtestToView, setBacktestToView] = React.useState(null)
  const [editingPattern, setEditingPattern] = React.useState<Pattern | null>(null)
  const [editingPatternIsTradingHouse, setEditingPatternIsTradingHouse] = React.useState(false)
  // Patterns ⇄ Indicators view toggle (BuildArea header); editingIndicator
  // mirrors editingPattern — null = list, {} = creating, {editId} = editing
  const [builderTab, setBuilderTab] = React.useState<'patterns' | 'indicators'>('patterns')
  const [editingIndicator, setEditingIndicator] = React.useState<{
    editId?: string
    // Fresh doc from create-on-entry, bridging the live-query latency window
    doc?: CustomIndicatorDoc
  } | null>(null)

  const canUseCustomIndicators = !!hasFeature?.(FeatureTags.CUSTOM_INDICATORS)
  const { customIndicators, error: customIndicatorsError } = useCustomIndicators({
    gcid: usermeta?.gcid,
    tradingHouse,
    enabled: canUseCustomIndicators,
  })

  // Fetch patterns from Firebase
  const [patterns, loading, error] = useCollectionData(
    query(
      collection(firebase_firestore, `usermeta/${usermeta?.gcid}/patterns`),
      where('tradingHouse', '==', tradingHouse),
      where('active', '==', true),
      orderBy('name', 'asc')
    )
  )

  // Fetch trading house patterns
  const [tradingHousePatterns, tradingHousePatternsLoading, tradingHousePatternsError] =
    useCollectionData(
      query(
        collection(firebase_firestore, `tradingHouses/${tradingHouse}/standardPatterns`),
        where('active', '==', true),
        orderBy('name', 'asc')
      )
    )

  // Fuzzy search function
  const fuzzySearch = (patternName: string, query: string): boolean => {
    if (!query) return true

    patternName = patternName?.toLowerCase() || ''
    query = query.toLowerCase()

    let patternIndex = 0
    let queryIndex = 0

    // Allow skipping characters in pattern but require all query chars to match in order
    while (patternIndex < patternName.length && queryIndex < query.length) {
      if (patternName[patternIndex] === query[queryIndex]) {
        queryIndex++
      }
      patternIndex++
    }

    // If we matched all query characters, it's a match
    return queryIndex === query.length
  }

  // Filter patterns based on fuzzy search
  const filterBySearch = React.useCallback(
    (list: any[]) => {
      if (!list) return []
      if (!searchTerm) return list
      return list.filter(
        (p: any) =>
          p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          fuzzySearch(p.name, searchTerm)
      )
    },
    [searchTerm]
  )

  const filteredPatterns = React.useMemo(
    () => filterBySearch(patterns || []),
    [patterns, filterBySearch]
  )
  const filteredTradingHousePatterns = React.useMemo(
    () => filterBySearch((tradingHousePatterns || []) as any[]),
    [tradingHousePatterns, filterBySearch]
  )

  useEffect(() => {
    const groups: { label: string; indicators: Indicator[] }[] = []
    const list: Indicator[] = []

    // Custom indicators at top: the picker offers only active ones, but the
    // flat resolver list includes deactivated ones so saved conditions that
    // reference them still render and validate.
    if (canUseCustomIndicators && customIndicators.length > 0) {
      const activeEntries = customIndicators
        .filter((ci) => ci.active)
        .map(buildCompositeRegistryEntry)
      if (activeEntries.length > 0) {
        groups.push({ label: 'My Indicators', indicators: activeEntries })
      }
      list.push(...customIndicators.map(buildCompositeRegistryEntry))
    }

    // DTU at top if permitted
    if (hasFeature?.(FeatureTags.DTU_INDICATORS)) {
      groups.push({ label: 'Day Trade U', indicators: gtfIndicators })
      list.push(...gtfIndicators)
    }

    // Standard if permitted
    if (hasFeature?.(FeatureTags.STANDARD_INDICATORS)) {
      groups.push({ label: 'Standard', indicators: standardIndicators })
      list.push(...standardIndicators)
    }

    // Price is always available
    groups.push({ label: 'Price', indicators: defaultStartingIndicators })
    list.push(...defaultStartingIndicators)

    setStartingGroupIndicators(groups)
    setStartingIndicators(list)
  }, [hasFeature, canUseCustomIndicators, customIndicators])

  const getIndicatorByValue = useCallback(
    (value: string | null | undefined): Indicator | undefined => {
      return startingIndicators.find((indicator) => indicator.value === value)
    },
    [startingIndicators]
  )

  return (
    <PatternContext.Provider
      value={{
        getIndicatorByValue,
        startingIndicators,
        startingGroupIndicators,
        saveAsStandardPattern,
        setSaveAsStandardPattern,
        searchTerm,
        setSearchTerm,
        patterns: filteredPatterns,
        patternsLoading: loading,
        patternsError: error,
        tradingHousePatterns: filteredTradingHousePatterns,
        tradingHousePatternsLoading,
        tradingHousePatternsError,
        backtestToView,
        setBacktestToView,
        focusedBacktest,
        setFocusedBacktest,
        editingPattern,
        setEditingPattern,
        editingPatternIsTradingHouse,
        setEditingPatternIsTradingHouse,
        customIndicators,
        customIndicatorsError,
        canUseCustomIndicators,
        builderTab,
        setBuilderTab,
        editingIndicator,
        setEditingIndicator,
        // Unfiltered (search-independent) pattern list, e.g. for usage counts
        allPatterns: patterns || [],
      }}
    >
      <PatternSidePanelProvider>
        <DndProvider backend={HTML5Backend}>{children}</DndProvider>
      </PatternSidePanelProvider>
    </PatternContext.Provider>
  )
}

interface patternModifers {
  getIndicatorByValue: (value: string | null | undefined) => Indicator | undefined
  startingIndicators: Indicator[]
  startingGroupIndicators: { label: string; indicators: Indicator[] }[]
  saveAsStandardPattern: boolean
  setSaveAsStandardPattern: React.Dispatch<React.SetStateAction<boolean>>
  searchTerm: string
  setSearchTerm: React.Dispatch<React.SetStateAction<string>>
  patterns: any[]
  patternsLoading: boolean
  patternsError: any
  tradingHousePatterns: any[]
  tradingHousePatternsLoading: boolean
  tradingHousePatternsError: any
  backtestToView: any
  setBacktestToView: React.Dispatch<React.SetStateAction<any>>
  focusedBacktest: any
  setFocusedBacktest: React.Dispatch<React.SetStateAction<any>>
  editingPattern: Pattern | null
  setEditingPattern: React.Dispatch<React.SetStateAction<Pattern | null>>
  editingPatternIsTradingHouse: boolean
  setEditingPatternIsTradingHouse: React.Dispatch<React.SetStateAction<boolean>>
  customIndicators: CustomIndicatorDoc[]
  customIndicatorsError: unknown
  canUseCustomIndicators: boolean
  builderTab: 'patterns' | 'indicators'
  setBuilderTab: React.Dispatch<React.SetStateAction<'patterns' | 'indicators'>>
  editingIndicator: { editId?: string; doc?: CustomIndicatorDoc } | null
  setEditingIndicator: React.Dispatch<
    React.SetStateAction<{ editId?: string; doc?: CustomIndicatorDoc } | null>
  >
  allPatterns: any[]
}

export const PatternContext = createContext<patternModifers>({} as patternModifers)

// Helper hook for easier context usage
export const usePatternContext = () => useContext(PatternContext)
